import { useState, useEffect, memo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import {
  PlusCircleIcon,
  XIcon,
  Loader2,
  Upload,
  CheckCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";

interface BrandFormData {
  name: string;
  description?: string;
  website_url?: string;
  country_of_origin?: string;
  founded_year?: number;
  verified?: boolean;
}

interface BrandAddFormProps {
  isEditing?: boolean;
  existingBrand?: any;
  onEditSuccess?: () => void;
}

export default memo(function BrandAddForm({ 
  isEditing = false, 
  existingBrand = null,
  onEditSuccess 
}: BrandAddFormProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedLogo, setUploadedLogo] = useState<string>("");
  
  // Form state
  const [formData, setFormData] = useState<BrandFormData>({
    name: existingBrand?.name || "",
    description: existingBrand?.description || "",
    website_url: existingBrand?.website_url || "",
    country_of_origin: existingBrand?.country_of_origin || "",
    founded_year: existingBrand?.founded_year || undefined,
    verified: existingBrand?.verified || false,
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle input changes
  const handleInputChange = (field: keyof BrandFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = field === 'founded_year' ? 
      (e.target.value ? parseInt(e.target.value) : undefined) : 
      e.target.value;
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (field: keyof BrandFormData) => (checked: boolean) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Brand name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Brand name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Brand name must be less than 100 characters';
    }
    
    if (formData.website_url && formData.website_url.trim() && !formData.website_url.match(/^https?:\/\/.+/)) {
      newErrors.website_url = 'Please enter a valid URL starting with http:// or https://';
    }
    
    if (formData.founded_year && (formData.founded_year < 1800 || formData.founded_year > new Date().getFullYear())) {
      newErrors.founded_year = 'Please enter a valid year between 1800 and current year';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle logo upload (simplified - using a placeholder URL for now)
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // In a real implementation, you would upload to Cloudinary or another service
      const fakeUrl = URL.createObjectURL(file);
      setUploadedLogo(fakeUrl);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error("You must be logged in to add brands");
      }

      // Prepare brand data
      const brandData = {
        name: formData.name,
        description: formData.description || null,
        website_url: formData.website_url || null,
        country_of_origin: formData.country_of_origin || null,
        founded_year: formData.founded_year || null,
        logo_url: uploadedLogo || null,
        verified: formData.verified || false,
        created_by: user.id,
      };

      let result;
      if (isEditing && existingBrand) {
        // Update existing brand
        const { data: updatedBrand, error: updateError } = await supabase
          .from('brands')
          .update(brandData)
          .eq('id', existingBrand.id)
          .select()
          .single();

        if (updateError) throw updateError;
        result = updatedBrand;
      } else {
        // Create new brand
        const { data: newBrand, error: createError } = await supabase
          .from('brands')
          .insert([brandData])
          .select()
          .single();

        if (createError) throw createError;
        result = newBrand;
      }

      // Handle success based on mode
      if (isEditing) {
        console.log('✅ Brand updated successfully');
        if (onEditSuccess) {
          onEditSuccess();
        } else {
          navigate('/supplier-dashboard?tab=brands&success=true');
        }
      } else {
        // Reset form and show success message for new brand creation
        setFormData({
          name: "",
          description: "",
          website_url: "",
          country_of_origin: "",
          founded_year: undefined,
          verified: false,
        });
        setUploadedLogo("");
        setSuccess(true);
        
        // Redirect to supplier dashboard after short delay
        setTimeout(() => {
          navigate('/supplier-dashboard?tab=brands&success=true');
        }, 2000);
      }
      
    } catch (error) {
      console.error('Error adding brand:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to add brand. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('validation')) {
          errorMessage = 'Please fill in all required fields correctly.';
        } else if (error.message.includes('name')) {
          errorMessage = 'Brand name is required and must be at least 2 characters.';
        } else if (error.message.includes('duplicate') || error.message.includes('unique')) {
          errorMessage = 'A brand with this name already exists.';
        } else if (error.message.includes('auth')) {
          errorMessage = 'You must be logged in to add brands.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success && !isEditing) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-100">
                Brand Added Successfully!
              </h3>
              <p className="text-green-700 dark:text-green-300">
                Your brand has been submitted and will be reviewed by our team.
              </p>
            </div>
            <Button onClick={() => setSuccess(false)} variant="outline">
              Add Another Brand
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>
            Enter the basic details about the brand
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Brand Name <span className="text-red-500">*</span></Label>
              <Input 
                id="name"
                placeholder="Enter brand name" 
                value={formData.name}
                onChange={handleInputChange('name')}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="country_of_origin">Country of Origin</Label>
              <Input 
                id="country_of_origin"
                placeholder="e.g., Japan, Germany, India" 
                value={formData.country_of_origin || ''}
                onChange={handleInputChange('country_of_origin')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea 
              id="description"
              placeholder="Brief description of the brand" 
              rows={3}
              value={formData.description || ''}
              onChange={handleInputChange('description')}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="website_url">Website URL</Label>
              <Input 
                id="website_url"
                placeholder="https://www.example.com" 
                value={formData.website_url || ''}
                onChange={handleInputChange('website_url')}
                className={errors.website_url ? 'border-red-500' : ''}
              />
              {errors.website_url && (
                <p className="text-sm text-red-600">{errors.website_url}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="founded_year">Founded Year</Label>
              <Input 
                id="founded_year"
                type="number" 
                placeholder="e.g., 1985"
                min="1800"
                max={new Date().getFullYear()}
                value={formData.founded_year || ''}
                onChange={handleInputChange('founded_year')}
                className={errors.founded_year ? 'border-red-500' : ''}
              />
              {errors.founded_year && (
                <p className="text-sm text-red-600">{errors.founded_year}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logo Upload */}
      <Card>
        <CardHeader>
          <CardTitle>Brand Logo</CardTitle>
          <CardDescription>
            Upload a logo for the brand (optional)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-center w-full">
              <label
                htmlFor="logo-upload"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {uploadedLogo ? (
                    <img src={uploadedLogo} alt="Logo preview" className="w-16 h-16 object-contain mb-2" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mb-3 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        PNG, JPG or SVG (MAX. 5MB)
                      </p>
                    </>
                  )}
                </div>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
              </label>
            </div>
            {uploadedLogo && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setUploadedLogo("")}
              >
                <XIcon className="h-4 w-4 mr-1" />
                Remove Logo
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Verification Status (Admin only feature - placeholder) */}
      <Card>
        <CardHeader>
          <CardTitle>Verification</CardTitle>
          <CardDescription>
            Brand verification status (will be reviewed by our team)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-row items-start space-x-3 space-y-0">
            <Checkbox
              id="verified"
              checked={formData.verified || false}
              onCheckedChange={handleCheckboxChange('verified')}
              disabled // Users can't verify their own brands
            />
            <div className="space-y-1 leading-none">
              <Label htmlFor="verified">
                Verified Brand
              </Label>
              <p className="text-sm text-muted-foreground">
                This brand will be marked as verified after admin review
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/supplier-dashboard?tab=brands')}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="w-full sm:w-auto"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isEditing ? "Updating..." : "Adding Brand..."}
            </>
          ) : (
            <>
              <PlusCircleIcon className="mr-2 h-4 w-4" />
              {isEditing ? "Update Brand" : "Add Brand"}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}); 